import { Button, Div, FormBuilder, FormBuilderType } from "@grassrootly/rn-core";
import { useEffect, useState } from "react";
import { GameType, IGameForm, newGameForm } from "../../../screens/new-game/NewGame.types";
import { IGame } from "@grassrootly/api";
import { useSeasonSelector } from "../../../screens/new-game/useSeasonSelector";
import SeasonSelector from "../../../screens/new-game/SeasonSelector";

type GameUpdateData = Omit<IGameForm, 'seasonId'> & { seasonId?: number | null };

export type EditGameDetailsProps = {
  game: IGame;
  updateGameDetails: (data: GameUpdateData) => void;
  updateGameInProgress: boolean;
};

export default function EditGameDetails({ game, updateGameDetails, updateGameInProgress }: EditGameDetailsProps) {
  const [gameFormState, setGameFormState] = useState<FormBuilderType<IGameForm> | null>(null);
  
  // Season selector integration
  const { seasonOptions, selectedSeasonId, setSelectedSeasonId, loadingSeasons } = useSeasonSelector(game.group_id || undefined);

  useEffect(() => {
    const baseForm = {
      datetime: { ...newGameForm(game.game_type).datetime, defaultValue: new Date(game.datetime || "") },
      location: { ...newGameForm(game.game_type).location, defaultValue: game.location || "" },
      notes: { ...newGameForm(game.game_type).notes, defaultValue: game.notes || "" },
    };

    // Add opponent team field for COMPETITIVE games
    if (game.game_type === GameType.COMPETITIVE) {
      const competitiveForm = newGameForm(game.game_type);
      // Determine opponent team based on playing_as_host
      const opponentTeamName = game.playing_as_host ? game.visitor_team_name : game.host_team_name;
      
      setGameFormState({
        ...baseForm,
        opponentTeam: { 
          ...competitiveForm.opponentTeam!, 
          defaultValue: opponentTeamName || "" 
        },
      });
    } else {
      setGameFormState(baseForm);
    }
  }, [game]);

  // Set initial season selection based on game data
  useEffect(() => {
    if (seasonOptions.length > 0 && game.season_id) {
      // Try to find the season that matches the current game's season_id
      const currentSeason = seasonOptions.find(option => 
        option.value === game.season_id
      );
      
      if (currentSeason) {
        setSelectedSeasonId(currentSeason.value);
      }
    }
  }, [seasonOptions, game.season_id, setSelectedSeasonId]);

  const handleSubmit = (formData: IGameForm) => {
    // Remove seasonId from formData since we handle it separately
    const { seasonId, ...gameData } = formData;
    updateGameDetails({
      ...gameData,
      seasonId: selectedSeasonId || null
    });
  };

  return (
    <Div classNames={["flex1", "ph8", "pt-{16}", "pb-{40}", "bgNeutralDarkerDark"]}>
      {/* Season selector only shows if there are seasons available */}
      <SeasonSelector
        seasonOptions={seasonOptions}
        selectedSeasonId={selectedSeasonId}
        onSeasonChange={setSelectedSeasonId}
        loading={loadingSeasons}
      />
      
      {gameFormState && (
        <FormBuilder
          form={gameFormState}
          onSubmit={handleSubmit}
          submitControl={(submit) => (
            <Button
              title="Save"
              classNames={["btnBlock", "mt-{24}"]}
              isLoading={updateGameInProgress}
              disabled={updateGameInProgress}
              type="button"
              onPress={submit}
            />
          )}
        />
      )}
    </Div>
  );
}
